/* @flow */


import { getRandom, apiName, apiVersion } from '../utils';
import bidbase from './bidbase';
import { logError } from '../utils/logger';
import { isJSONSupported } from '../utils/browserutil';
// getCCPA no longer needed - using _getPrivacyString() from bidbase
import { isFetchSupported, fetchJSON } from '../utils/fetch';
import { PREBID_UID_CRITEO } from '../idproviders/idpconstants';
import { getUserID, getRTBUserEids } from '../idproviders/idputils';
import { extend } from '../utils/coreutils';

/**
 * @module bidders/sovrn
 * @requires module:logger
 */

/**
 * @typedef {object} hbPosData
 */

declare type sovBidRequestCache = {
  id: string,
  impid: {
    [string]: string
  }
};

/**
 * @description Implements functionality for Sovrn header bidding
 * @augments _bidbase
 * @requires module:utils
 * @requires module:jsonp
 */
class _sovrnbid extends bidbase {
  _bidRequest: sovBidRequestCache;

  _config: sovrnBidConfig;

  constructor(name: string = 'sovrn') {
    super(name);
    this._bidRequest = {
      id: '',
      impid: {}
    };
    this._mandatoryConfigs = ['bidUrl'];
    this._posId = 'tagid';
    this._isSupported = isJSONSupported() && isFetchSupported();
    this._beaconUrlId = 'sBeacon';
  }

  /**
   * Process RTB bid response. Response format:
   * resp.id - bid request id
   * resp.seatbid[0].bid[N].price - ecpm value
   * resp.seatbid[0].bid[N].adm - ad creative
   * resp.seatbid[0].bid[N].nurl - impression pixel
   *
   * @param {Array<string>} posList - List of HB positions in response
   * @param {object} resp - Response object from back-end
   * @param {object.<string, hbPosData>} ret - Return data
   * @returns {object.<string, hbPosData>} - Response after processing
   * @access private
   */
  _processBidResponse(
    posList: Array<string>,
    resp: RTBBidResponse,
    ret: hbPartnerData
  ): hbPartnerData {
    if (!this._isValidRTBResponse(resp) || resp.id !== this._bidRequest.id) {
      return ret;
    }
    resp.seatbid[0].bid.forEach(bid => {
      if (bid.impid) {
        this._bidResponses[bid.impid] = bid;
      }
    });

    // Note: For sovrn, we need to call decodeURIComponent. Refer Prebid.js
    posList.forEach(pos => {
      const impid = this._bidRequest.impid[pos];
      const res: RTBBidPosResponse = this._bidResponses[impid];
      if (res && res.price && res.nurl && res.adm) {
        this._handleBidPosData(
          ret,
          pos,
          res.price,
          impid,
          decodeURIComponent(res.adm),
          res.nurl
        );
      }
    });
    return ret;
  }

  _getIDPInfo(): anyObject {
    const { user: { eids = [] } = {} } = getRTBUserEids();
    const tpid = eids.map(({ source, uids: [{ id: uid = '' } = {}] = [] }) => ({
      source,
      uid
    }));
    const criteoId = getUserID(PREBID_UID_CRITEO);
    if (eids.length === 0) {
      return {};
    }
    let ret = {
      user: {
        ext: {
          eids,
          tpid
        }
      }
    };
    if (criteoId.length) {
      ret = extend(true, {}, ret, {
        // eslint-disable-next-line camelcase
        user: { ext: { prebid_criteoid: criteoId } }
      });
    }
    return ret;
  }

  _getPrivacy(): anyObject {
    const privacyString = this._getPrivacyString();
    return {
      regs: { ext: privacyString }
    };
  }

  /**
   * get bids for given positions
   *
   * @param {Array<string>} posList - Array of positions to bid
   * @param {object.<string, hbPosData>} ret - Return data
   * @returns {Promise<object.<string, hbPosData>>} - Promise
   * @access private
   */
  _getAds(posList: Array<string>, ret: hbPartnerData): Promise<hbPartnerData> {
    this._bidResponses = {};

    // generate unique request id
    this._bidRequest.id = getRandom();
    const loc: Location = window.location;

    // build params for bid request
    const params = extend(
      true,
      {},
      {
        id: this._bidRequest.id,
        site: {
          domain: loc.host,
          page: loc.pathname
        },
        imp: posList.map(pos => {
          // generate unique id for this impresssion (position)
          this._bidRequest.impid[pos] = getRandom();
          return {
            id: this._bidRequest.impid[pos],
            banner: {
              w: this._config.posTokenMap[pos].width,
              h: this._config.posTokenMap[pos].height
            },
            tagid: this.getPosKey(pos)
          };
        })
      },
      this._getIDPInfo(),
      this._getPrivacy()
    );

    const opt = {
      data: {
        src: `${apiName()}-${apiVersion()}`
      },
      timeout: this._config.timeout,
      logPrefix: this._name
    };
    const req = {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify(params)
    };
    return fetchJSON(this._config.bidUrl, opt, req).then(
      (data: RTBBidResponse) => this._processBidResponse(posList, data, ret),
      error => {
        logError(`${this._name} Failed to get bids: ${error || ''}`);
        return ret;
      }
    );

    /* const reqParams = {
      data: {
        br: JSON.stringify(params),
        // eslint-disable-next-line camelcase
        us_privacy: getCCPA()
      },
      timeout: this._config.timeout,
      logPrefix: posList.join(',')
    };
    return JSONP(this._config.bidUrl, reqParams).then(
      (data: RTBBidResponse) => this._processBidResponse(posList, data, ret),
      error => {
        logError(`${this._name} Failed to get bids: ${error || ''}`);
        return ret;
      }
    ); */
  }
}

const sovrnbid: _sovrnbid = new _sovrnbid();
export default sovrnbid;
